/**
 * One language of the intro (spec 2026-10-23 §5): the video in a phone frame,
 * "Replace video", and the description for screen readers.
 * English is required; Spanish is optional and falls back to English.
 */
import { DESCRIPTION_MAX_CHARS, type ExperienceLocale } from '../lib/contract';
import { LANGUAGE_NAME, type FinalizedMedia } from '../lib/intro-state';
import { formatBytes } from '../lib/upload-flow';
import { formatDuration, shapeLabel } from '../lib/video-client-checks';
import { PhoneFramePreview } from './PhoneFramePreview';
import { VideoUploadPanel } from './VideoUploadPanel';

/** The intro tab's ids, unchanged since Task 10 (its upload panels keep their own default too). */
const INTRO_ID_PREFIX = 'app-experience';

interface Props {
  locale: ExperienceLocale;
  apiUrl: string;
  media: FinalizedMedia | null;
  /** The video members get now for this language (null = none). */
  liveUrl: string | null;
  description: string;
  canWrite: boolean;
  onMedia: (media: FinalizedMedia | null) => void;
  onDescription: (text: string) => void;
  onBusyChange: (busy: boolean) => void;
  /**
   * Start of the element ids. The two tabs stay mounted together, so the
   * welcome tab's panels need their own prefix. The default keeps the intro
   * tab's ids.
   */
  idPrefix?: string;
}

export function LanguagePanel({
  locale,
  apiUrl,
  media,
  liveUrl,
  description,
  canWrite,
  onMedia,
  onDescription,
  onBusyChange,
  idPrefix = INTRO_ID_PREFIX,
}: Props) {
  const name = LANGUAGE_NAME[locale];
  const required = locale === 'en';
  const isNew = (media?.url ?? null) !== liveUrl;
  const descId = `${idPrefix}-${locale}-description`;
  const empty = description.trim() === '';
  // One hint under the description: what to do first, a warning, or a reminder.
  const hint = !media
    ? { warn: false, text: 'Add a video first. Then write its description.' }
    : empty
      ? { warn: true, text: 'The description is empty. You can publish, but people who use a screen reader will not know what the video shows.' }
      : isNew && liveUrl
        ? { warn: false, text: 'You replaced the video. Make sure that the description still agrees with it.' }
        : null;

  return (
    <section aria-labelledby={`${descId}-heading`} className="space-y-4 rounded-lg border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 id={`${descId}-heading`} className="text-base font-semibold">
            {name} {required ? '(required)' : '(optional)'}
          </h4>
          {!required && (
            <p className="text-xs text-muted-foreground">
              If there is no Spanish video, people who use the app in Spanish see the English video.
            </p>
          )}
        </div>
        {isNew && (
          <span className="rounded-full border border-blue-300 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
            {media ? 'New — not published' : 'Removed — not published'}
          </span>
        )}
      </div>

      <PhoneFramePreview
        media={media}
        languageName={name}
        emptyText={required ? 'No video yet.' : 'No Spanish video. The English video plays.'}
      />
      {media && (
        <p className="text-center text-xs text-muted-foreground">
          {formatDuration(media.duration_ms)} · {shapeLabel(media.width, media.height)} · {media.width} × {media.height} ·{' '}
          {formatBytes(media.bytes)}
        </p>
      )}

      <VideoUploadPanel
        apiUrl={apiUrl}
        languageName={name}
        hasVideo={media !== null}
        showReady={isNew && media !== null}
        disabled={!canWrite}
        onReady={onMedia}
        onBusyChange={onBusyChange}
        idPrefix={idPrefix === INTRO_ID_PREFIX ? undefined : `${idPrefix}-upload`}
      />

      {!required && media && (
        <button
          type="button"
          onClick={() => onMedia(null)}
          disabled={!canWrite}
          className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          Remove the Spanish video
        </button>
      )}

      <div>
        <label htmlFor={descId} className="mb-1 block text-sm font-medium">
          Description for screen readers ({name})
        </label>
        <p id={`${descId}-help`} className="mb-2 text-xs text-muted-foreground">
          Write what the video shows and says. People who cannot see or hear the video get this text.
        </p>
        <textarea
          id={descId}
          value={description}
          onChange={(e) => onDescription(e.target.value)}
          maxLength={DESCRIPTION_MAX_CHARS}
          rows={4}
          disabled={!canWrite || !media}
          aria-describedby={`${descId}-help ${descId}-count`}
          className="block w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <div className="mt-1 flex flex-wrap justify-between gap-2 text-xs">
          <span className={hint?.warn ? 'text-amber-800 dark:text-amber-300' : 'text-muted-foreground'}>{hint?.text}</span>
          <span id={`${descId}-count`} className="text-muted-foreground">
            {description.length} / {DESCRIPTION_MAX_CHARS} characters
          </span>
        </div>
      </div>
    </section>
  );
}
